/**
 * V1.9.2 — Floor-Plan Symbols catalog.
 *
 * Architectural-standard top-down symbols the user can drag from the
 * Symbol Palette onto the 2D plan. Each entry:
 *   id          unique slug used as the persistence key
 *   category    "furniture" | "appliance" | "plumbing" | "stairs" | "misc"
 *   label       human-readable display name
 *   width_m     default footprint width  in metres (along local +x)
 *   depth_m     default footprint depth  in metres (along local +z)
 *   keywords    free-text array (search/filter)
 *   render      ({ size, mode, selected }) => SVG element
 *     - size:    { px, scale_m_per_px } — px = display width AND height in
 *       palette swatches (square), or the full px width for canvas
 *     - mode:    "thumb" | "canvas"  — thumb is small + monochrome,
 *       canvas is full real-world scale
 *     - selected (canvas mode only) — true to draw the cyan selection halo
 *
 * Symbols are drawn in their LOCAL coordinate system: origin at the
 * top-left corner, +x right, +z down. The canvas renderer rotates
 * around the symbol center using `transform`, so the local geometry
 * never needs to know about rotation.
 *
 * Sizes are based on standard residential/commercial fixture catalogs
 * (Architectural Graphic Standards). Defaults are mid-range — users
 * can rescale per-instance post-drop (V1.9.3 feature, deferred).
 */
import React from "react";

const stroke = "#1f2937"; // dark line for architectural mode
const fill = "#ffffff";

// Helper — render a labelled rect with text inside (for appliance code labels)
const codeBox = (w, h, code, sw = 1.2) => (
  <>
    <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth={sw} />
    <text x={w / 2} y={h / 2 + 3.5} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fontWeight="700" fill={stroke}>
      {code}
    </text>
  </>
);

// Each `render` returns the INNER SVG content — caller wraps it in <svg>.
const SYMBOLS = {
  // ============ FURNITURE ============
  bed_single: {
    category: "furniture", label: "Single Bed",
    width_m: 1.0, depth_m: 2.0,
    keywords: ["bed", "single", "twin", "mattress"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        {/* Pillow at top */}
        <rect x={w * 0.15} y={h * 0.04} width={w * 0.7} height={h * 0.13} fill="none" stroke={stroke} strokeWidth="0.8" />
        {/* Blanket fold line */}
        <line x1="0.5" y1={h * 0.32} x2={w - 0.5} y2={h * 0.32} stroke={stroke} strokeWidth="0.6" />
      </>
    ),
  },
  bed_double: {
    category: "furniture", label: "Double Bed",
    width_m: 1.5, depth_m: 2.0,
    keywords: ["bed", "double", "queen", "mattress"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        {/* Two pillows */}
        <rect x={w * 0.08} y={h * 0.04} width={w * 0.4} height={h * 0.12} fill="none" stroke={stroke} strokeWidth="0.8" />
        <rect x={w * 0.52} y={h * 0.04} width={w * 0.4} height={h * 0.12} fill="none" stroke={stroke} strokeWidth="0.8" />
        <line x1="0.5" y1={h * 0.32} x2={w - 0.5} y2={h * 0.32} stroke={stroke} strokeWidth="0.6" />
      </>
    ),
  },
  bed_king: {
    category: "furniture", label: "King Bed",
    width_m: 1.93, depth_m: 2.03,
    keywords: ["bed", "king", "mattress"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <rect x={w * 0.05} y={h * 0.04} width={w * 0.42} height={h * 0.12} fill="none" stroke={stroke} strokeWidth="0.8" />
        <rect x={w * 0.53} y={h * 0.04} width={w * 0.42} height={h * 0.12} fill="none" stroke={stroke} strokeWidth="0.8" />
        <line x1="0.5" y1={h * 0.32} x2={w - 0.5} y2={h * 0.32} stroke={stroke} strokeWidth="0.6" />
      </>
    ),
  },
  sofa: {
    category: "furniture", label: "Sofa (3-seater)",
    width_m: 2.1, depth_m: 0.9,
    keywords: ["sofa", "couch", "three seater", "seating"],
    render: ({ w, h }) => (
      <>
        {/* Backrest band */}
        <rect x="0.5" y="0.5" width={w - 1} height={h * 0.3} fill={fill} stroke={stroke} strokeWidth="1.2" />
        {/* Seat cushions x3 */}
        <rect x="0.5" y={h * 0.3} width={w - 1} height={h * 0.7 - 0.5} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <line x1={w / 3} y1={h * 0.3} x2={w / 3} y2={h - 0.5} stroke={stroke} strokeWidth="0.8" />
        <line x1={(2 * w) / 3} y1={h * 0.3} x2={(2 * w) / 3} y2={h - 0.5} stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  chair_single: {
    category: "furniture", label: "Single Seater Chair",
    width_m: 0.85, depth_m: 0.85,
    keywords: ["chair", "armchair", "single seater"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h * 0.3} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <rect x="0.5" y={h * 0.3} width={w - 1} height={h * 0.7 - 0.5} fill={fill} stroke={stroke} strokeWidth="1.2" />
      </>
    ),
  },
  coffee_table: {
    category: "furniture", label: "Coffee Table",
    width_m: 1.2, depth_m: 0.6,
    keywords: ["table", "coffee", "living room"],
    render: ({ w, h }) => (
      <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
    ),
  },
  end_table: {
    category: "furniture", label: "End Table",
    width_m: 0.55, depth_m: 0.55,
    keywords: ["table", "end", "nightstand", "side"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.28} fill="none" stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  desk: {
    category: "furniture", label: "Desk",
    width_m: 1.4, depth_m: 0.7,
    keywords: ["desk", "table", "workstation"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        {/* Chair stub */}
        <rect x={w * 0.35} y={h + 1} width={w * 0.3} height={h * 0.4} fill="none" stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  dressing_table: {
    category: "furniture", label: "Dressing Table",
    width_m: 1.0, depth_m: 0.45,
    keywords: ["dresser", "vanity", "dressing"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <line x1={w / 3} y1="0.5" x2={w / 3} y2={h - 0.5} stroke={stroke} strokeWidth="0.6" />
        <line x1={(2 * w) / 3} y1="0.5" x2={(2 * w) / 3} y2={h - 0.5} stroke={stroke} strokeWidth="0.6" />
      </>
    ),
  },

  // ============ APPLIANCES ============
  refrigerator: {
    category: "appliance", label: "Refrigerator",
    width_m: 0.75, depth_m: 0.7,
    keywords: ["fridge", "refrigerator", "kitchen"],
    render: ({ w, h }) => codeBox(w, h, "REF"),
  },
  stove_oven: {
    category: "appliance", label: "Stove/Oven",
    width_m: 0.76, depth_m: 0.65,
    keywords: ["stove", "oven", "range", "cooktop"],
    render: ({ w, h }) => {
      const cx = w / 2, cy = h / 2, r = Math.min(w, h) * 0.12;
      const off = Math.min(w, h) * 0.22;
      const burner = (x, y) => (
        <circle cx={x} cy={y} r={r} fill="none" stroke={stroke} strokeWidth="0.8" />
      );
      return (
        <>
          <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {burner(cx - off, cy - off)}
          {burner(cx + off, cy - off)}
          {burner(cx - off, cy + off)}
          {burner(cx + off, cy + off)}
        </>
      );
    },
  },
  dishwasher: {
    category: "appliance", label: "Dishwasher",
    width_m: 0.6, depth_m: 0.6,
    keywords: ["dishwasher", "dw"],
    render: ({ w, h }) => codeBox(w, h, "DW"),
  },
  washer: {
    category: "appliance", label: "Washer",
    width_m: 0.6, depth_m: 0.6,
    keywords: ["washer", "laundry"],
    render: ({ w, h }) => codeBox(w, h, "W"),
  },
  dryer: {
    category: "appliance", label: "Dryer",
    width_m: 0.6, depth_m: 0.6,
    keywords: ["dryer", "laundry"],
    render: ({ w, h }) => codeBox(w, h, "D"),
  },
  sink_single: {
    category: "appliance", label: "Kitchen Sink",
    width_m: 0.75, depth_m: 0.55,
    keywords: ["sink", "kitchen", "single"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <ellipse cx={w / 2} cy={h / 2} rx={(w - 6) / 2} ry={(h - 6) / 2} fill="none" stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  sink_double: {
    category: "appliance", label: "Double Sink",
    width_m: 0.85, depth_m: 0.55,
    keywords: ["sink", "kitchen", "double"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <ellipse cx={w * 0.27} cy={h / 2} rx={w * 0.18} ry={(h - 6) / 2} fill="none" stroke={stroke} strokeWidth="0.8" />
        <ellipse cx={w * 0.73} cy={h / 2} rx={w * 0.18} ry={(h - 6) / 2} fill="none" stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  cabinet: {
    category: "appliance", label: "Cabinet",
    width_m: 0.6, depth_m: 0.6,
    keywords: ["cabinet", "cupboard", "storage"],
    render: ({ w, h }) => codeBox(w, h, "CAB"),
  },
  countertop: {
    category: "appliance", label: "Countertop",
    width_m: 1.5, depth_m: 0.6,
    keywords: ["countertop", "counter", "cntp"],
    render: ({ w, h }) => codeBox(w, h, "CNTP"),
  },

  // ============ PLUMBING ============
  toilet: {
    category: "plumbing", label: "Toilet",
    width_m: 0.4, depth_m: 0.7,
    keywords: ["toilet", "wc", "water closet"],
    render: ({ w, h }) => {
      // Tank on the back + oval bowl
      const tankH = h * 0.32;
      const bowlR_w = w * 0.40, bowlR_h = h * 0.28;
      return (
        <>
          <rect x={w * 0.1} y="0.5" width={w * 0.8} height={tankH} fill={fill} stroke={stroke} strokeWidth="1.2" />
          <ellipse cx={w / 2} cy={tankH + (h - tankH) * 0.55} rx={bowlR_w} ry={bowlR_h} fill={fill} stroke={stroke} strokeWidth="1.2" />
        </>
      );
    },
  },
  bidet: {
    category: "plumbing", label: "Bidet",
    width_m: 0.36, depth_m: 0.6,
    keywords: ["bidet"],
    render: ({ w, h }) => (
      <ellipse cx={w / 2} cy={h / 2} rx={(w - 2) / 2} ry={(h - 2) / 2} fill={fill} stroke={stroke} strokeWidth="1.2" />
    ),
  },
  lavatory: {
    category: "plumbing", label: "Lavatory Sink",
    width_m: 0.5, depth_m: 0.45,
    keywords: ["lavatory", "bathroom sink", "vanity"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <ellipse cx={w / 2} cy={h * 0.55} rx={w * 0.32} ry={h * 0.32} fill="none" stroke={stroke} strokeWidth="0.8" />
        <line x1={w / 2 - 2} y1={h * 0.12} x2={w / 2 + 2} y2={h * 0.12} stroke={stroke} strokeWidth="1" />
      </>
    ),
  },
  bathtub: {
    category: "plumbing", label: "Bathtub (Recessed)",
    width_m: 1.5, depth_m: 0.75,
    keywords: ["bath", "tub", "bathtub"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <rect x="3" y="3" width={w - 6} height={h - 6} rx={Math.min(w, h) * 0.18} fill="none" stroke={stroke} strokeWidth="0.8" />
        {/* faucet dot */}
        <circle cx={w * 0.08} cy={h / 2} r="1.5" fill={stroke} />
      </>
    ),
  },
  bathtub_corner: {
    category: "plumbing", label: "Corner Bathtub",
    width_m: 1.5, depth_m: 1.5,
    keywords: ["bath", "tub", "corner"],
    render: ({ w, h }) => (
      <>
        <path d={`M 0.5 0.5 L ${w - 0.5} 0.5 L ${w - 0.5} ${h - 0.5} L 0.5 ${h - 0.5} Z`} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <path d={`M 4 ${h - 4} Q 4 4 ${w - 4} 4`} fill="none" stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  shower_stall: {
    category: "plumbing", label: "Shower Stall",
    width_m: 0.9, depth_m: 0.9,
    keywords: ["shower", "stall"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <line x1="2" y1="2" x2={w - 2} y2={h - 2} stroke={stroke} strokeWidth="0.8" />
        <line x1={w - 2} y1="2" x2="2" y2={h - 2} stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },
  shower_corner: {
    category: "plumbing", label: "Corner Shower",
    width_m: 0.9, depth_m: 0.9,
    keywords: ["shower", "corner"],
    render: ({ w, h }) => (
      <>
        <path d={`M 0.5 0.5 L ${w - 0.5} 0.5 L ${w - 0.5} ${h * 0.4} Q ${w - 0.5} ${h - 0.5} ${w * 0.4} ${h - 0.5} L 0.5 ${h - 0.5} Z`}
          fill={fill} stroke={stroke} strokeWidth="1.2" />
        {/* triangular flow icon */}
        <path d={`M ${w * 0.65} ${h * 0.25} L ${w * 0.85} ${h * 0.35} L ${w * 0.55} ${h * 0.4} Z`} fill="none" stroke={stroke} strokeWidth="0.8" />
      </>
    ),
  },

  // ============ STAIRS ============
  stairs_straight: {
    category: "stairs", label: "Straight Stairs",
    width_m: 1.1, depth_m: 3.6,
    keywords: ["stairs", "straight", "linear"],
    render: ({ w, h }) => {
      const treads = 12;
      const lines = [];
      for (let i = 1; i < treads; i++) {
        const y = (h / treads) * i;
        lines.push(<line key={i} x1="0.5" y1={y} x2={w - 0.5} y2={y} stroke={stroke} strokeWidth="0.6" />);
      }
      return (
        <>
          <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {lines}
          {/* Up arrow */}
          <line x1={w / 2} y1={h * 0.85} x2={w / 2} y2={h * 0.1} stroke={stroke} strokeWidth="0.8" markerEnd="url(#stair-arrow)" />
        </>
      );
    },
  },
  stairs_l_shape: {
    category: "stairs", label: "L-Shaped Stairs",
    width_m: 2.4, depth_m: 2.4,
    keywords: ["stairs", "L", "landing"],
    render: ({ w, h }) => {
      const armW = w * 0.42;
      const armH = h * 0.42;
      const lines = [];
      const lower = h - armH;
      for (let i = 1; i < 6; i++) {
        const y = lower + (armH / 6) * i;
        lines.push(<line key={`r-${i}`} x1="0.5" y1={y} x2={w - 0.5} y2={y} stroke={stroke} strokeWidth="0.6" />);
      }
      for (let i = 1; i < 6; i++) {
        const x = (armW / 6) * i;
        lines.push(<line key={`v-${i}`} x1={x} y1="0.5" x2={x} y2={lower} stroke={stroke} strokeWidth="0.6" />);
      }
      return (
        <>
          {/* Vertical arm (top) */}
          <rect x="0.5" y="0.5" width={armW} height={lower - 0.5} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {/* Horizontal arm (bottom) */}
          <rect x="0.5" y={lower} width={w - 1} height={armH - 0.5} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {lines}
        </>
      );
    },
  },
  stairs_u_shape: {
    category: "stairs", label: "U-Shaped Stairs",
    width_m: 2.4, depth_m: 3.0,
    keywords: ["stairs", "U", "switchback"],
    render: ({ w, h }) => {
      const armW = w * 0.42;
      const lines = [];
      for (let i = 1; i < 8; i++) {
        const y = (h / 8) * i;
        lines.push(<line key={`l-${i}`} x1="0.5" y1={y} x2={armW} y2={y} stroke={stroke} strokeWidth="0.6" />);
        lines.push(<line key={`r-${i}`} x1={w - armW} y1={y} x2={w - 0.5} y2={y} stroke={stroke} strokeWidth="0.6" />);
      }
      return (
        <>
          <rect x="0.5" y="0.5" width={armW} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
          <rect x={w - armW} y="0.5" width={armW} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {/* Landing */}
          <rect x={armW} y={h * 0.3} width={w - 2 * armW} height={h * 0.4} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {lines}
        </>
      );
    },
  },
  stairs_spiral: {
    category: "stairs", label: "Spiral Stairs",
    width_m: 1.6, depth_m: 1.6,
    keywords: ["stairs", "spiral", "circular"],
    render: ({ w, h }) => {
      const cx = w / 2, cy = h / 2;
      const R = Math.min(w, h) / 2 - 1;
      const wedges = [];
      const N = 12;
      for (let i = 0; i < N; i++) {
        const a0 = (i * Math.PI * 2) / N - Math.PI / 2;
        const a1 = ((i + 1) * Math.PI * 2) / N - Math.PI / 2;
        const x0 = cx + Math.cos(a0) * R, y0 = cy + Math.sin(a0) * R;
        const x1 = cx + Math.cos(a1) * R, y1 = cy + Math.sin(a1) * R;
        wedges.push(
          <path key={i} d={`M ${cx} ${cy} L ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} Z`} fill="none" stroke={stroke} strokeWidth="0.6" />,
        );
      }
      return (
        <>
          <circle cx={cx} cy={cy} r={R} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {wedges}
          <circle cx={cx} cy={cy} r={R * 0.15} fill={stroke} />
        </>
      );
    },
  },
  stairs_winding: {
    category: "stairs", label: "Winding Stairs",
    width_m: 1.2, depth_m: 2.8,
    keywords: ["stairs", "winding", "winder"],
    render: ({ w, h }) => {
      const lines = [];
      for (let i = 1; i < 10; i++) {
        const y = (h / 10) * i;
        const skew = i < 4 ? (w * 0.05 * (4 - i)) : 0;
        lines.push(<line key={i} x1={skew} y1={y} x2={w - skew} y2={y} stroke={stroke} strokeWidth="0.6" />);
      }
      return (
        <>
          <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
          {lines}
        </>
      );
    },
  },

  // ============ MISC ============
  closet: {
    category: "misc", label: "Closet",
    width_m: 1.2, depth_m: 0.6,
    keywords: ["closet", "wardrobe", "storage"],
    render: ({ w, h }) => (
      <>
        <rect x="0.5" y="0.5" width={w - 1} height={h - 1} fill={fill} stroke={stroke} strokeWidth="1.2" />
        <line x1="3" y1={h * 0.5} x2={w - 3} y2={h * 0.5} stroke={stroke} strokeWidth="0.6" strokeDasharray="2,2" />
      </>
    ),
  },
};

// CATEGORY metadata — display order + label + icon glyph.
export const CATEGORIES = [
  { id: "furniture", label: "Furniture",   icon: "🛋", color: "border-brass-500"   },
  { id: "appliance", label: "Appliances",  icon: "🍳", color: "border-amber-500"  },
  { id: "plumbing",  label: "Plumbing",    icon: "🚿", color: "border-blue-500"   },
  { id: "stairs",    label: "Stairs",      icon: "🪜", color: "border-emerald-500"},
  { id: "misc",      label: "Misc",        icon: "🧰", color: "border-zinc-500"   },
];

export const SYMBOL_CATALOG = SYMBOLS;

export const symbolsInCategory = (catId) =>
  Object.entries(SYMBOLS)
    .filter(([_, s]) => s.category === catId)
    .map(([id, s]) => ({ id, ...s }));

export const getSymbol = (id) => SYMBOLS[id] || null;
